<template>
    <div class="modal fade" :id="modalId" data-bs-backdrop="static" aria-labelledby="filter-proposal-modal-title" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header border-bottom-0 pb-0">
                    <div class="d-flex align-items-start w-100">
                        <div class="flex-grow-1" style="min-width: 0; margin-right: 1rem;">
                            <h3 class="mt-2 mb-2 text-center text-sm-start">
                                Filter Suggestion
                            </h3>
                        </div>
                        <div class="d-flex align-items-center" style="flex: 0 0 auto;">
                            <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal"
                                data-bs-toggle="tooltip" title="Close"
                                @click="handleClose">
                                <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt="">
                            </button>
                        </div>
                    </div>
                </div>
                <div class="modal-body">
                    <div v-if="loading" class="text-center py-4">
                        <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-2">Loading suggestion...</p>
                    </div>

                    <div v-else-if="error" class="alert alert-danger">
                        {{ error }}
                    </div>

                    <div v-else class="d-flex flex-column gap-3">
                        <!-- Dataset Info -->
                        <div>
                            <label class="form-label">Dataset:</label> <br>
                            <strong>{{ dataSet?.name || 'Unknown Dataset' }}</strong>
                        </div>

                        <!-- Proposed Filter -->
                        <FilterDefinitionCard
                            :filter="localProposal"
                            title="Proposed Filter"
                            :show-expand-icon="false"
                            empty-message="No filter proposal available."
                        />

                        <!-- Reasoning in its own card -->
                        <div v-show="localProposal?.reasoning" class="filter-definition-card">
                            <div class="card segment-proposal-card">
                                <div class="card-body p-3">
                                    <h4 class="h6 fw-semibold mb-0"><img class="icon me-2" src="/img/icons/avid-icons/edna.svg" alt="">Reasoning</h4>
                                    <div class="formatted-logic text-muted mt-2" style="white-space: pre-line;">{{ localProposal?.reasoning }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer pt-1">
                    <button 
                        v-if="!loading && !error"
                        type="button" 
                        class="btn btn-secondary" 
                        @click="submitFeedback"
                        :disabled="submitting"
                    >
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <img v-else class="icon me-2" src="/img/icons/avid-icons/edna.svg" alt="">
                        Refine
                    </button>
                    <button 
                        v-if="!loading && !error && localProposal"
                        type="button" 
                        class="btn btn-primary reg-btn" 
                        @click="acceptProposal"
                        :disabled="submitting"
                    >
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Accept
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useNotifications } from '../../composables/useNotifications';
import FilterDefinitionCard from './FilterDefinitionCard.vue';

export default defineComponent({
    name: 'FilterProposalModal',
    components: { FilterDefinitionCard },
    props: {
        client: {
            type: Object,
            required: true,
        },
        dataSetId: {
            type: Number,
            required: true,
        },
        // For file-based proposals (without a dataset)
        fileId: {
            type: Number,
            default: null,
        },
        // Pre-loaded proposal (for file-based mode)
        proposal: {
            type: Object,
            default: null,
        },
        // Pre-loaded reasoning (for file-based mode)
        reasoning: {
            type: String,
            default: null,
        },
        previousResponseId: {
            type: String,
            default: null,
        },
        // Source type and subtype for file-based feedback
        sourceType: {
            type: String,
            default: null,
        },
        sourceSubtype: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            modalId: `filter-proposal-modal-${this.fileId || this.dataSetId}-${Date.now()}`,
            loading: false, // Start as false, will be set to true when show() is called
            error: null,
            localProposal: null,
            dataSet: null,
            submitting: false,
            
            hasLoaded: false, // Track if we've already attempted to load
        };
    },
    mounted() {
        // Initialize notifications composable
        const { showNotification } = useNotifications();
        this.showNotification = showNotification;
        
        // Only load proposal if modal is being shown (not just mounted)
        // The show() method will trigger loading if needed
    },
    watch: {
        // Watch for changes to the proposal prop (e.g., when a refined proposal arrives)
        proposal: {
            handler(newProposal) {
                if (newProposal) {
                    this.localProposal = { ...newProposal };
                    if (this.reasoning && !this.localProposal.reasoning) {
                        this.localProposal.reasoning = this.reasoning;
                    }
                    this.error = null;
                    this.loading = false;
                }
            },
            immediate: false,
            deep: true,
        },
        reasoning(newReasoning) {
            if (newReasoning && this.localProposal) {
                this.localProposal.reasoning = newReasoning;
            }
        },
    },
    methods: {
        async loadProposal() {
            // If we have a proposal passed as prop (file-based mode), use it directly
            if (this.proposal) {
                this.localProposal = { ...this.proposal };
                if (this.reasoning && !this.localProposal.reasoning) {
                    this.localProposal.reasoning = this.reasoning;
                }
                this.loading = false;
                return;
            }

            // Otherwise, load from API (dataset-based mode)
            try {
                // Load dataset to get the proposal
                const response = await window.axios.get(`/api/filter-mapping/${this.client.url}/${this.dataSetId}`);
                this.dataSet = response.data.data_set;
                
                // Extract proposal from metadata
                this.localProposal = response.data.proposal || null;
                // Store reasoning separately if available
                if (response.data.reasoning && this.localProposal) {
                    this.localProposal.reasoning = response.data.reasoning;
                } else if (response.data.reasoning && !this.localProposal) {
                    // If we have reasoning but no proposal, create a minimal proposal structure
                    console.warn('FilterProposalModal: Has reasoning but no proposal structure');
                }

                if (!this.localProposal) {
                    console.warn('FilterProposalModal: No proposal found in response', response.data);
                }

                this.loading = false;
            } catch (error) {
                console.error('Error loading filter proposal:', error);
                this.error = 'Failed to load filter proposal. Please try again.';
                this.loading = false;
            }
        },
        async acceptProposal() {
            if (!this.localProposal) {
                this.showNotification({
                    text: 'No filter suggestions were found.',
                    type: 'error'
                });
                return;
            }

            this.submitting = true;

            // For file-based mode, just emit and close - no API call needed
            if (this.fileId) {
                // Close modal
                const modal = bootstrap.Modal.getInstance(document.getElementById(this.modalId));
                if (modal) {
                    modal.hide();
                }

                // Emit the filter for parent to update
                this.$emit('accepted', {
                    file_id: this.fileId,
                    filter: this.localProposal,
                });

                this.submitting = false;
                return;
            }

            // Dataset-based mode - call API
            const data = {
                data_set_id: this.dataSetId,
                filter: this.localProposal,
            };

            if (this.dataSet?.settings?.ai_filter_suggestion_metadata?.response_id) {
                data.response_id = this.dataSet.settings.ai_filter_suggestion_metadata.response_id;
            }

            try {
                const response = await window.axios.post(`/api/filter-mapping/${this.client.url}/accept`, data);
                
                // Close modal
                const modal = bootstrap.Modal.getInstance(document.getElementById(this.modalId));
                if (modal) {
                    modal.hide();
                }

                // Clear the pending action
                if (window.__pendingFilterProposal) {
                    delete window.__pendingFilterProposal;
                }

                // Emit the filter for parent to update
                this.$emit('accepted', {
                    data_set_id: this.dataSetId,
                    filter: response.data.data_set?.filter || this.localProposal,
                });
            } catch (error) {
                console.error('Error accepting filter proposal:', error);
                this.showNotification({
                    text: error.response?.data?.message || 'Failed to accept filter. Please try again.',
                    type: 'error'
                });
            } finally {
                this.submitting = false;
            }
        },
        async submitFeedback() {
            // Always prompt for user context, even if feedback field has content
            Swal.fire({
                title: 'Refine Filter Suggestion',
                // Important: Bootstrap modals trap focus. Render the SweetAlert inside the modal
                // so the textarea can receive focus/clicks.
                target: document.getElementById(this.modalId),
                html: `
                    <p class="mb-2">How can we improve the current filter suggestion?</p>
                    <textarea id="filter-context" class="form-control" placeholder="E.g., Exclude X type as well; Include Y records; etc." style="width: 100%; min-height: 100px;"></textarea>
                `,
                showCancelButton: true,
                confirmButtonText: 'Submit',
                cancelButtonText: 'Cancel',
                didOpen: () => {
                    const textarea = Swal.getPopup()?.querySelector('#filter-context');
                    if (textarea) {
                        textarea.focus();
                    }
                },
                preConfirm: () => {
                    const context = document.getElementById('filter-context').value;
                    return context || null;
                }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const userContext = result.value || '';
                    
                    this.submitting = true;

                    try {
                        let data = {};
                        let apiUrl = '';
                        
                        // For file-based mode, use feedback endpoint
                        if (this.fileId) {
                            if (!this.sourceType) {
                                throw new Error('Source type is required for file-based feedback');
                            }
                            if (!this.previousResponseId) {
                                throw new Error('Previous response ID is required for feedback');
                            }
                            data = {
                                file_id: this.fileId,
                                source_type: this.sourceType,
                                source_subtype: this.sourceSubtype || null,
                                feedback: userContext,
                                previous_response_id: this.previousResponseId,
                                current_filter: this.localProposal, // Send current filter state for AI context
                            };
                            apiUrl = `/api/file-mapping/${this.client.url}/feedback`;
                        } else {
                            // For dataset-based mode, use data_set_id endpoint
                            data = {
                                data_set_id: this.dataSetId,
                                feedback: userContext,
                                previous_response_id: this.dataSet?.settings?.ai_filter_suggestion_metadata?.response_id || this.previousResponseId,
                                current_filter: this.localProposal, // Send current filter state for AI context
                            };
                            apiUrl = `/api/filter-mapping/${this.client.url}/feedback`;
                        }

                        await window.axios.post(apiUrl, data);

                        this.showNotification({
                            icon: '/img/icons/nav-icons/Edna.svg',
                            text: 'We are refining the filter suggestion based on your feedback. You\'ll be notified when it\'s ready!',
                            type: 'info'
                        });

                        // Close modal
                        const modal = bootstrap.Modal.getInstance(document.getElementById(this.modalId));
                        if (modal) {
                            modal.hide();
                        }
                        // Clear the pending action when modal is closed
                        if (window.__pendingFilterProposal) {
                            delete window.__pendingFilterProposal;
                        }
                    } catch (error) {
                        console.error('Error submitting feedback:', error);
                        this.showNotification({
                            text: error.response?.data?.message || 'Failed to submit feedback. Please try again.',
                            type: 'error'
                        });
                    } finally {
                        this.submitting = false;
                    }
                }
            });
        },
        show() {
            // For dataset-based modals (no proposal prop), always reload to get latest data
            // For file-based modals (with proposal prop), the watcher will handle updates
            if (!this.proposal) {
                // Dataset-based: always reload from API to get latest proposal
                // Reset state to ensure fresh load
                this.loading = true;
                this.error = null;
                this.hasLoaded = false; // Reset to force reload
                this.loadProposal();
                this.hasLoaded = true;
            } else if (!this.hasLoaded) {
                // File-based: load initial proposal from prop
                this.loading = true;
                this.hasLoaded = true;
                this.loadProposal();
            }
            
            // Method to programmatically show the modal
            const modalElement = document.getElementById(this.modalId);
            if (modalElement && window.bootstrap) {
                let modal = window.bootstrap.Modal.getInstance(modalElement);
                if (!modal) {
                    modal = new window.bootstrap.Modal(modalElement);
                }
                modal.show();
            } else {
                console.error('FilterProposalModal: Could not find modal element', {
                    modalId: this.modalId,
                    elementExists: !!document.getElementById(this.modalId),
                });
            }
        },
        handleClose() {
            // Clear the pending action when modal is closed
            if (window.__pendingFilterProposal) {
                delete window.__pendingFilterProposal;
            }
        },
    },
});
</script>
